import { useCallback, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import EmptyPage from '../../empty-page/emptyPage';
import CommonTable from '../../reusable-ui-components/commonTable/CommonTable';
import Pagination from '../../pagination/Pagination';
import { orgStayDashboardFun } from '../../../api/orgCampAPI';
import errorHandle from '../../../utils/errorHandle';
import { checkInColumns } from '../orgStay/stayColumns';

/*
 * Organizer > Camp > Dashboard: check-ins (upcoming / today / completed)
 * and the totals, from GET /org/camp/stay/dashboard.
 */
const KPIS = [
    { key: 'today', label: 'Check-ins today' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'completed', label: 'Completed stays' },
    { key: 'totalBookings', label: 'Total bookings' }
];

const CampDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selected = searchParams.get('date') || 'next';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [stats, setStats] = useState({});
    const [totalCount, setTotalCount] = useState(0);

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);
            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) delete current[key];
                else current[key] = params[key];
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        let gone = false;
        setLoading(true);
        orgStayDashboardFun({ date: selected, limit, offset: offSet })
            .then((res) => {
                if (gone) return;
                setTableData(res?.data?.data || []);
                setStats(res?.data?.stats || {});
                setTotalCount(res?.data?.totalCount || 0);
            })
            .catch((error) => !gone && errorHandle(error))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [selected, offSet]);

    const paginationClick = (number) => {
        if (Math.ceil(totalCount / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='kpi-container'>
                {KPIS.map((k, index) => (
                    <div className={`kpi-card kpi-card-${index + 1}`} key={k.key}>
                        <h1>{stats[k.key] ?? 0}</h1>
                        <h4>{k.label}</h4>
                    </div>
                ))}
            </div>
            <div className='DashListOfUpcoming'>
                <div className='customOptions'>
                    <div className='sticky-container'>
                        <select
                            name='date'
                            className='select'
                            value={selected}
                            onChange={(e) => updateURL({ date: e.target.value, page: 1 })}
                        >
                            <option value='next'>Upcoming check-ins</option>
                            <option value='today'>Today</option>
                            <option value='previous'>Completed</option>
                        </select>
                    </div>
                </div>
                {tableData.length === 0 && !loading ? (
                    <EmptyPage />
                ) : (
                    <>
                        <CommonTable data={tableData} columns={checkInColumns} setRowClickId={() => {}} />
                        <Pagination
                            paginationClick={paginationClick}
                            numberOfRows={totalCount}
                            offSet={offSet}
                            pageNumber={pageNumber}
                            limit={limit}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default CampDashboard;
